import { useState, useEffect } from 'react';
import { collection, addDoc, query, getDocs, orderBy, where, updateDoc, writeBatch, doc, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';
import { isEqual } from 'lodash';
import CreateWalletTransaction from './CreateWalletTransaction';
import { exportToJson, exportToCsv } from '../../utils/exportHelpers';
import { logAdminAction } from '../../utils/auditLogger';
import { ACTION_TYPES } from '../../utils/auditLogger';

const DATE_FILTERS = [
  { value: 'today', label: 'Mai utalások' },
  { value: 'yesterday', label: 'Tegnapi utalások' },
  { value: 'thisWeek', label: 'Ezen a héten' },
  { value: 'thisMonth', label: 'Ebben a hónapban' },
  { value: 'custom', label: 'Egyedi időszak' }
];

export default function WalletTransactionManager() {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0,
    processed: 0,
    successful: 0,
    skipped: 0,
    failed: 0
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [editingDescription, setEditingDescription] = useState(null);

  useEffect(() => {
    if (searchTerm.length >= 3) {
      fetchTransactions(true); // Összes betöltése kereséskor
    } else {
      fetchTransactions(false); // Limitált betöltés
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchTransactions(searchTerm.length >= 3);
  }, [dateFilter, startDate, endDate]);

  const fetchTransactions = async () => {
    if (!searchTerm) {
      setInitialLoading(true);
    }
    setLoading(true);
    try {
      const q = getDateFilterQuery(dateFilter);
      if (!q) {
        setTransactions([]);
        return;
      }

      const snapshot = await getDocs(q);
      const txs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate(),
        last_updated: doc.data().last_updated?.toDate()
      }));
      
      setTransactions(txs);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Hiba történt a tranzakciók betöltésekor');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const getDateFilterQuery = (filter) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const baseQuery = collection(db, 'walletTransactions');
    
    switch (filter) {
      case 'today':
        return query(
          baseQuery,
          where('created_at', '>=', today),
          where('created_at', '<', tomorrow),
          orderBy('created_at', 'desc')
        );
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return query(
          baseQuery,
          where('created_at', '>=', yesterday),
          where('created_at', '<', today),
          orderBy('created_at', 'desc')
        );
      case 'thisWeek':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return query(
          baseQuery,
          where('created_at', '>=', weekStart),
          orderBy('created_at', 'desc')
        );
      case 'thisMonth':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return query(
          baseQuery,
          where('created_at', '>=', monthStart),
          orderBy('created_at', 'desc')
        );
      case 'custom':
        if (!startDate || !endDate) return null;
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setDate(end.getDate() + 1);
        return query(
          baseQuery,
          where('created_at', '>=', start),
          where('created_at', '<', end),
          orderBy('created_at', 'desc')
        );
      default:
        return query(baseQuery, orderBy('created_at', 'desc'));
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setStats({
      total: 0,
      processed: 0,
      successful: 0,
      skipped: 0,
      failed: 0
    });

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const transactions = data.transactions || [];
      
      setStats(prev => ({ ...prev, total: transactions.length }));

      for (const [index, tx] of transactions.entries()) {
        try {
          const transactionData = {
            transaction_id: tx.transaction_id,
            amount: tx.amount,
            sender: {
              email: tx.sender?.email || '',
              user_id: tx.sender?.user_id || '',
            },
            recipient: {
              email: tx.recipient?.email || '',
              user_id: tx.recipient?.user_id || '',
            },
            last_updated: new Date()
          };

          // Ellenőrizzük, hogy létezik-e már ez a tranzakció
          const existingTxQuery = query(
            collection(db, 'walletTransactions'), 
            where('transaction_id', '==', tx.transaction_id)
          );
          const existingTxDocs = await getDocs(existingTxQuery);

          if (existingTxDocs.empty) {
            // Ha nem létezik, új tranzakciót hozunk létre
            transactionData.created_at = new Date();
            transactionData.status = 'completed';
            await addDoc(collection(db, 'walletTransactions'), transactionData);
            setStats(prev => ({
              ...prev,
              processed: index + 1,
              successful: prev.successful + 1
            }));
          } else {
            // Ha létezik, ellenőrizzük a változásokat
            const existingDoc = existingTxDocs.docs[0];
            const existingData = existingDoc.data();
            
            // Összehasonlítjuk a meglévő és új adatokat
            const hasChanges = !isEqual(existingData.amount, transactionData.amount) || 
                             !isEqual(existingData.sender, transactionData.sender) ||
                             !isEqual(existingData.recipient, transactionData.recipient);

            if (hasChanges) {
              // Csak akkor frissítünk, ha van változás
              await updateDoc(existingDoc.ref, {
                amount: transactionData.amount,
                sender: transactionData.sender,
                recipient: transactionData.recipient,
                last_updated: new Date()
              });
              setStats(prev => ({
                ...prev,
                processed: index + 1,
                successful: prev.successful + 1
              }));
            } else {
              // Ha nincs változás, csak növeljük a feldolgozott számot
              setStats(prev => ({
                ...prev,
                processed: index + 1,
                skipped: (prev.skipped || 0) + 1
              }));
            }
          }

        } catch (error) {
          console.error('Error importing transaction:', error);
          setStats(prev => ({
            ...prev,
            processed: index + 1,
            failed: prev.failed + 1
          }));
        }
      }

      toast.success(`Importálás befejezve: 
        ${stats.successful} sikeres, 
        ${stats.skipped || 0} kihagyott (nem változott), 
        ${stats.failed} sikertelen`
      );
      fetchTransactions();

    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error('Hiba történt a fájl feldolgozása során');
    } finally {
      setImporting(false);
    }
  };

  const filteredTransactions = transactions.filter(tx => 
    tx.sender?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.recipient?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExportJson = () => {
    const exportData = transactions.map(tx => ({
      transaction_id: tx.transaction_id,
      amount: tx.amount,
      sender: tx.sender,
      recipient: tx.recipient,
      status: tx.status,
      type: tx.type,
      created_at: tx.created_at?.toDate?.().toISOString(),
      coin: tx.coin,
      balance_before: tx.balance_before
    }));
    
    exportToJson(exportData, `wallet_transactions_${new Date().toISOString()}.json`);
  };

  const handleExportCsv = () => {
    const exportData = transactions.map(tx => ({
      transaction_id: tx.transaction_id,
      amount: tx.amount,
      sender_email: tx.sender?.email,
      sender_id: tx.sender?.user_id,
      recipient_email: tx.recipient?.email,
      recipient_id: tx.recipient?.user_id,
      status: tx.status,
      type: tx.type,
      created_at: tx.created_at?.toDate?.().toISOString(),
      coin: tx.coin
    }));
    
    exportToCsv(exportData, `wallet_transactions_${new Date().toISOString()}.csv`);
  };

  const handleRevertTransaction = async (transaction) => {
    if (!window.confirm('Biztosan visszavonja ezt a tranzakciót?')) {
      return;
    }

    try {
      // 1. Új revert tranzakció létrehozása (ellentétes irányú)
      const revertData = {
        transaction_id: `REVERT_${transaction.transaction_id}`,
        amount: transaction.amount, // Az összeg ugyanaz marad
        sender: transaction.recipient, // Felcseréljük a küldőt és fogadót
        recipient: transaction.sender,
        created_at: new Date(),
        status: 'completed',
        type: 'revert',
        original_transaction: transaction.id,
        coin: transaction.amount.split(' ')[1]
      };

      // 2. Egyenlegek visszaállítása
      const amount = parseFloat(transaction.amount);
      const coin = transaction.amount.split(' ')[1];

      // Lekérjük a jelenlegi felhasználókat
      const senderRef = query(
        collection(db, 'walletUsers'),
        where('user_id', '==', transaction.recipient.user_id)
      );
      const recipientRef = query(
        collection(db, 'walletUsers'),
        where('user_id', '==', transaction.sender.user_id)
      );

      const [senderSnapshot, recipientSnapshot] = await Promise.all([
        getDocs(senderRef),
        getDocs(recipientRef)
      ]);

      const senderDoc = senderSnapshot.docs[0];
      const recipientDoc = recipientSnapshot.docs[0];

      // Batch használata a tranzakció atomicitásának biztosításához
      const batch = writeBatch(db);

      // Eredeti tranzakció megjelölése visszavontként
      batch.update(doc(db, 'walletTransactions', transaction.id), {
        reverted: true,
        reverted_at: new Date()
      });

      // Új revert tranzakció létrehozása
      const revertRef = doc(collection(db, 'walletTransactions'));
      batch.set(revertRef, revertData);

      // Egyenlegek frissítése
      if (senderDoc) {
        const currentBalance = parseFloat(senderDoc.data().details?.balances?.[coin] || 0);
        batch.update(senderDoc.ref, {
          [`details.balances.${coin}`]: (currentBalance - amount).toString()
        });
      }

      if (recipientDoc) {
        const currentBalance = parseFloat(recipientDoc.data().details?.balances?.[coin] || 0);
        batch.update(recipientDoc.ref, {
          [`details.balances.${coin}`]: (currentBalance + amount).toString()
        });
      }

      await batch.commit();

      await logAdminAction(ACTION_TYPES.TRANSACTION.REVERT, {
        transactionId: transaction.transaction_id,
        originalAmount: transaction.amount,
        sender: transaction.sender,
        recipient: transaction.recipient,
        revertedAt: new Date().toISOString()
      });

      toast.success('Tranzakció sikeresen visszavonva');
      fetchTransactions(); // Frissítjük a listát

    } catch (error) {
      console.error('Error reverting transaction:', error);
      toast.error('Hiba történt a tranzakció visszavonása során');
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Icons.Loader className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Wallet Tranzakciók</h1>
        <p className="mt-1 text-sm text-gray-500">Importált wallet tranzakciók kezelése</p>
      </div>

      {/* Import és Keresés */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 grid grid-cols-3 gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Keresés tranzakció ID, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            />
            {initialLoading ? (
              <Icons.Loader2 className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 animate-spin" />
            ) : (
              <Icons.Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
            )}
          </div>

          <div>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            >
              {DATE_FILTERS.map(filter => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
          </div>

          {dateFilter === 'custom' && (
            <div className="flex gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Icons.Plus className="h-5 w-5" />
            <span className="text-sm font-medium">Új tranzakció</span>
          </button>
          
          <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <Icons.Upload className="h-5 w-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Import</span>
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>

          <button
            onClick={handleExportJson}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Icons.Download className="h-5 w-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Export JSON</span>
          </button>

          <button
            onClick={handleExportCsv}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Icons.FileText className="h-5 w-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Statisztikai kártyák */}
      <div className="mb-6 grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg p-4 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium opacity-75">Összes tranzakció</p>
              <h3 className="text-2xl font-bold mt-1">{transactions.length}</h3>
            </div>
            <div className="bg-white/20 rounded-lg p-2">
              <Icons.BarChart className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-4 text-xs opacity-75">
            {dateFilter === 'today' ? 'Mai forgalom' : 'Időszak forgalma'}
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg p-4 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium opacity-75">Összes érték</p>
              <h3 className="text-2xl font-bold mt-1">
                {transactions.reduce((sum, tx) => {
                  const amount = parseFloat(tx.amount.split(' ')[0]);
                  return sum + (isNaN(amount) ? 0 : amount);
                }, 0).toFixed(2)}
                <span className="text-sm ml-1">FLCV</span>
              </h3>
            </div>
            <div className="bg-white/20 rounded-lg p-2">
              <Icons.DollarSign className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-4 text-xs opacity-75">
            Teljes forgalom az időszakban
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-lg p-4 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium opacity-75">Átlag érték</p>
              <h3 className="text-2xl font-bold mt-1">
                {(transactions.reduce((sum, tx) => {
                  const amount = parseFloat(tx.amount.split(' ')[0]);
                  return sum + (isNaN(amount) ? 0 : amount);
                }, 0) / (transactions.length || 1)).toFixed(2)}
                <span className="text-sm ml-1">FLCV</span>
              </h3>
            </div>
            <div className="bg-white/20 rounded-lg p-2">
              <Icons.TrendingUp className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-4 text-xs opacity-75">
            Átlagos tranzakció érték
          </div>
        </div>

        <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl shadow-lg p-4 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium opacity-75">Egyedi felhasználók</p>
              <h3 className="text-2xl font-bold mt-1">
                {new Set([
                  ...transactions.map(tx => tx.sender?.email),
                  ...transactions.map(tx => tx.recipient?.email)
                ]).size}
              </h3>
            </div>
            <div className="bg-white/20 rounded-lg p-2">
              <Icons.Users className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-4 text-xs opacity-75">
            Aktív felhasználók száma
          </div>
        </div>
      </div>

      {/* Tranzakciók listája */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tranzakció ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Küldő
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fogadó
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Összeg
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Megjegyzés
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Státusz
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentTransactions.map((tx) => (
                <tr key={tx.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {tx.transaction_id}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{tx.sender?.email}</div>
                    <div className="text-xs text-gray-500">ID: {tx.sender?.user_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{tx.recipient?.email}</div>
                    <div className="text-xs text-gray-500">ID: {tx.recipient?.user_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{tx.amount}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-pre-wrap text-sm text-gray-500 max-w-xs">
                    {editingDescription?.id === tx.id ? (
                      <div className="flex gap-2">
                        <textarea
                          value={editingDescription.text}
                          onChange={(e) => setEditingDescription({
                            id: tx.id,
                            text: e.target.value
                          })}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm"
                          rows={2}
                        />
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={async () => {
                              try {
                                await updateDoc(doc(db, 'walletTransactions', tx.id), {
                                  description: editingDescription.text.trim() || null
                                });
                                
                                await logAdminAction('transaction.update_description', {
                                  transactionId: tx.transaction_id,
                                  oldDescription: tx.description || '',
                                  newDescription: editingDescription.text.trim() || ''
                                });
                                
                                setEditingDescription(null);
                                fetchTransactions();
                                toast.success('Megjegyzés módosítva');
                              } catch (error) {
                                console.error('Error updating description:', error);
                                toast.error('Hiba történt a megjegyzés módosítása során');
                              }
                            }}
                            className="text-green-600 hover:text-green-700"
                          >
                            <Icons.Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setEditingDescription(null)}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            <Icons.X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="group flex items-start gap-2">
                        <span className="flex-1">{tx.description || '-'}</span>
                        <button
                          onClick={() => setEditingDescription({
                            id: tx.id,
                            text: tx.description || ''
                          })}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600"
                        >
                          <Icons.Edit2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {tx.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {!tx.reverted && (
                      <button
                        onClick={() => handleRevertTransaction(tx)}
                        className="text-red-600 hover:text-red-900 flex items-center gap-1 ml-auto"
                      >
                        <Icons.RotateCcw className="h-4 w-4" />
                        <span>Visszavonás</span>
                      </button>
                    )}
                    {tx.reverted && (
                      <span className="text-gray-500 text-xs">Visszavonva</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded ${
              currentPage === 1 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Icons.ChevronLeft className="h-5 w-5" />
          </button>

          <div className="flex space-x-1">
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
              ) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`px-3 py-1 rounded ${
                      currentPage === pageNumber
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 3 ||
                pageNumber === currentPage + 3
              ) {
                return <span key={pageNumber} className="px-2 text-gray-400">...</span>;
              }
              return null;
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded ${
              currentPage === totalPages 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Icons.ChevronRight className="h-5 w-5" />
          </button>

          <div className="text-sm text-gray-500">
            {t('common.showing', {
              from: indexOfFirstItem + 1,
              to: Math.min(indexOfLastItem, filteredTransactions.length),
              total: filteredTransactions.length
            })}
          </div>
        </div>
      )}

      {/* Import Progress Modal */}
      {importing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Importálás folyamatban...</h3>
            
            <div className="mb-4">
              <div className="h-2 bg-gray-200 rounded-full">
                <div 
                  className="h-2 bg-indigo-600 rounded-full transition-all duration-300"
                  style={{ width: `${(stats.processed / stats.total) * 100}%` }}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Feldolgozva:</p>
                <p className="font-medium">{stats.processed} / {stats.total}</p>
              </div>
              <div>
                <p className="text-green-600">Sikeres: {stats.successful}</p>
                <p className="text-blue-600">Kihagyott: {stats.skipped}</p>
                <p className="text-red-600">Sikertelen: {stats.failed}</p>
              </div>
            </div>
            
            <div className="mt-4 text-xs text-gray-500">
              {Math.round((stats.processed / stats.total) * 100)}% k��sz
            </div>
          </div>
        </div>
      )}

      {isCreateModalOpen && (
        <CreateWalletTransaction
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            fetchTransactions();
          }}
        />
      )}

      {transactions.length > 0 && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={async () => {
              const lastDoc = transactions[transactions.length - 1];
              const q = getDateFilterQuery(dateFilter);
              if (!q) return;

              const nextQuery = query(q, startAfter(lastDoc.created_at), limit(50));
              const snapshot = await getDocs(nextQuery);
              
              const newTxs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                created_at: doc.data().created_at?.toDate(),
                last_updated: doc.data().last_updated?.toDate()
              }));

              setTransactions(prev => [...prev, ...newTxs]);
            }}
            className="px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md"
          >
            További tranzakciók betöltése
          </button>
        </div>
      )}
    </div>
  );
} 